import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { icons } from '../icons.js';
import { Badge } from '../primitives/badge.js';
import { Button } from '../primitives/button.js';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, } from '../primitives/card.js';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '../primitives/dialog.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from '../primitives/dropdown-menu.js';
import { DateTimeInput } from '../primitives/date-time-input.js';
import { EmptyState } from '../primitives/empty-state.js';
import { Field } from '../primitives/field.js';
import { Input } from '../primitives/input.js';
import { Label } from '../primitives/label.js';
import { Select } from '../primitives/select.js';
import { Skeleton } from '../primitives/skeleton.js';
import { StatCard } from '../primitives/stat-card.js';
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow, } from '../primitives/table.js';
import { Textarea } from '../primitives/textarea.js';
import { Toast, ToastAction, ToastClose, ToastDescription, ToastProvider, ToastTitle, ToastViewport, } from '../primitives/toast.js';
/**
 * What kind of thing a primitive is. Used by the verification page to group, and by nothing else —
 * a primitive's behaviour never branches on its group.
 */
export const primitiveGroups = [
    'action',
    'surface',
    'data',
    'form',
    'overlay',
    'feedback',
];
const sampleRows = [
    { id: 3, nombre: 'ApuestaPR Carolina', ciudad: 'Carolina' },
    { id: 8, nombre: 'ApuestaPR Santurce', ciudad: 'San Juan' },
];
/** A dialog with its trigger, so the closed state is the one an app actually renders. */
const DialogExample = ({ defaultOpen = false, }) => (_jsxs(Dialog, { defaultOpen: defaultOpen, children: [_jsx(DialogTrigger, { asChild: true, children: _jsx(Button, { variant: "outline", children: "Ver detalles" }) }), _jsxs(DialogContent, { children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: "ApuestaPR Carolina" }), _jsx(DialogDescription, { children: "Ave. 65 de Infanter\u00EDa 1234, Carolina" })] }), _jsx(DialogFooter, { children: _jsx(DialogClose, { asChild: true, children: _jsx(Button, { variant: "secondary", children: "Cerrar" }) }) })] })] }));
const MenuExample = ({ defaultOpen = false }) => (_jsxs(DropdownMenu, { defaultOpen: defaultOpen, children: [_jsx(DropdownMenuTrigger, { asChild: true, children: _jsx(Button, { variant: "secondary", children: "Acciones" }) }), _jsxs(DropdownMenuContent, { children: [_jsx(DropdownMenuLabel, { children: "Sesi\u00F3n" }), _jsx(DropdownMenuItem, { children: "Revisar sesi\u00F3n" }), _jsx(DropdownMenuSeparator, {}), _jsx(DropdownMenuItem, { children: "Cerrar sesi\u00F3n" })] })] }));
export const primitiveRegistry = [
    {
        id: 'button',
        name: 'Button',
        module: 'button.tsx',
        group: 'action',
        summary: 'Five fills, three sizes, and the two states requirement 7.7 names: disabled by attribute, loading by aria-busy with focus kept.',
        states: [
            {
                id: 'variants',
                label: 'Variants',
                render: () => (_jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Button, { children: "Apostar" }), _jsx(Button, { variant: "secondary", children: "Ver m\u00E1s" }), _jsx(Button, { variant: "destructive", children: "Eliminar" }), _jsx(Button, { variant: "outline", children: "Cancelar" }), _jsx(Button, { variant: "ghost", children: "Omitir" })] })),
            },
            {
                id: 'sizes',
                label: 'Sizes',
                render: () => (_jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [_jsx(Button, { size: "sm", children: "Peque\u00F1o" }), _jsx(Button, { size: "md", children: "Mediano" }), _jsx(Button, { size: "lg", children: "Grande" })] })),
            },
            {
                id: 'disabled',
                label: 'Disabled',
                render: () => _jsx(Button, { disabled: true, children: "No disponible" }),
            },
            {
                id: 'loading',
                label: 'Loading',
                render: () => (_jsx(Button, { loading: true, loadingLabel: "Guardando la localidad\u2026", children: "Guardar" })),
            },
        ],
    },
    {
        id: 'card',
        name: 'Card',
        module: 'card.tsx',
        group: 'surface',
        summary: 'The shared surface. `interactive` swaps the resting elevation for the one hover treatment — requirement 7.5.',
        states: [
            {
                id: 'resting',
                label: 'Resting',
                render: () => (_jsxs(Card, { children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { children: "ApuestaPR Carolina" }), _jsx(CardDescription, { children: "Ave. 65 de Infanter\u00EDa 1234" })] }), _jsx(CardContent, { children: "Abierto todos los d\u00EDas." })] })),
            },
            {
                id: 'interactive',
                label: 'Interactive',
                render: () => (_jsxs(Card, { interactive: true, children: [_jsxs(CardHeader, { children: [_jsx(CardTitle, { children: "ApuestaPR Santurce" }), _jsx(CardDescription, { children: "Calle Lo\u00EDza 456" })] }), _jsx(CardFooter, { children: _jsx(Button, { size: "sm", variant: "outline", children: "Ver detalles" }) })] })),
            },
        ],
    },
    {
        id: 'table',
        name: 'Table',
        module: 'table.tsx',
        group: 'data',
        summary: 'Scoped header cells and a real caption, which is the table’s accessible name rather than a heading floating above it.',
        states: [
            {
                id: 'default',
                label: 'With caption and column headers',
                render: () => (_jsxs(Table, { children: [_jsx(TableCaption, { children: "Localidades sembradas" }), _jsx(TableHeader, { children: _jsxs(TableRow, { children: [_jsx(TableHead, { children: "Localidad" }), _jsx(TableHead, { children: "Pueblo" })] }) }), _jsx(TableBody, { children: sampleRows.map((row) => (_jsxs(TableRow, { children: [_jsx(TableCell, { children: row.nombre }), _jsx(TableCell, { children: row.ciudad })] }, row.id))) })] })),
            },
            {
                id: 'row-headers',
                label: 'With row headers',
                render: () => (_jsxs(Table, { children: [_jsx(TableCaption, { children: "Estado de la API" }), _jsxs(TableBody, { children: [_jsxs(TableRow, { children: [_jsx(TableHead, { scope: "row", children: "Servicio" }), _jsx(TableCell, { children: "ok" })] }), _jsxs(TableRow, { children: [_jsx(TableHead, { scope: "row", children: "Base de datos" }), _jsx(TableCell, { children: "reachable" })] })] })] })),
            },
        ],
    },
    {
        id: 'badge',
        name: 'Badge',
        module: 'badge.tsx',
        group: 'data',
        summary: 'Semantic fill plus visible text and an optional icon or shape, so editorial visibility never depends on colour alone.',
        states: [
            {
                id: 'default',
                label: 'Default',
                render: () => _jsx(Badge, { shape: "circle", children: "Borrador" }),
            },
            {
                id: 'populated',
                label: 'Populated semantic states',
                render: () => (_jsxs("div", { className: "flex flex-wrap gap-3", children: [_jsx(Badge, { variant: "accent", shape: "diamond", children: "Programado" }), _jsx(Badge, { variant: "outline", shape: "square", children: "Publicado" }), _jsx(Badge, { variant: "destructive", shape: "circle", children: "Archivado" })] })),
            },
            {
                id: 'icon',
                label: 'Icon and text',
                render: () => (_jsx(Badge, { variant: "accent", icon: _jsx("svg", { viewBox: "0 0 16 16", className: "h-4 w-4", focusable: "false", children: _jsx("path", { d: "M8 2v6l4 2", fill: "none", stroke: "currentColor", strokeWidth: "2" }) }), children: "Programado" })),
            },
        ],
    },
    {
        id: 'select',
        name: 'Select',
        module: 'select.tsx',
        group: 'form',
        summary: 'A native keyboard-operable select consuming Field ids, descriptions, required state, and invalid state.',
        states: [
            {
                id: 'default',
                label: 'Default',
                render: () => (_jsx(Field, { label: "Estado", children: (control) => (_jsxs(Select, { ...control, defaultValue: "", children: [_jsx("option", { value: "", children: "Selecciona un estado" }), _jsx("option", { value: "draft", children: "Borrador" }), _jsx("option", { value: "published", children: "Publicado" })] })) })),
            },
            {
                id: 'populated',
                label: 'Populated',
                render: () => (_jsx(Field, { label: "Estado", children: (control) => (_jsxs(Select, { ...control, defaultValue: "published", children: [_jsx("option", { value: "draft", children: "Borrador" }), _jsx("option", { value: "published", children: "Publicado" })] })) })),
            },
            {
                id: 'invalid',
                label: 'Invalid',
                render: () => (_jsx(Field, { label: "Estado", error: "Selecciona un estado.", children: (control) => (_jsxs(Select, { ...control, defaultValue: "", children: [_jsx("option", { value: "", children: "Selecciona un estado" }), _jsx("option", { value: "draft", children: "Borrador" })] })) })),
            },
            {
                id: 'disabled',
                label: 'Disabled',
                render: () => (_jsx(Field, { label: "Estado", children: (control) => (_jsx(Select, { ...control, disabled: true, defaultValue: "archived", children: _jsx("option", { value: "archived", children: "Archivado" }) })) })),
            },
        ],
    },
    {
        id: 'textarea',
        name: 'Textarea',
        module: 'textarea.tsx',
        group: 'form',
        summary: 'A resizable multiline control with the shared focus and non-colour invalid treatments.',
        states: [
            {
                id: 'default',
                label: 'Default',
                render: () => (_jsx(Field, { label: "Descripci\u00F3n", children: (control) => _jsx(Textarea, { ...control, placeholder: "Describe el contenido" }) })),
            },
            {
                id: 'populated',
                label: 'Populated',
                render: () => (_jsx(Field, { label: "Descripci\u00F3n", children: (control) => _jsx(Textarea, { ...control, defaultValue: "Contenido listo para publicar." }) })),
            },
            {
                id: 'invalid',
                label: 'Invalid',
                render: () => (_jsx(Field, { label: "Descripci\u00F3n", error: "La descripci\u00F3n es obligatoria.", children: (control) => _jsx(Textarea, { ...control }) })),
            },
            {
                id: 'disabled',
                label: 'Disabled',
                render: () => (_jsx(Field, { label: "Descripci\u00F3n", children: (control) => _jsx(Textarea, { ...control, disabled: true, defaultValue: "No editable" }) })),
            },
        ],
    },
    {
        id: 'date-time-input',
        name: 'DateTimeInput',
        module: 'date-time-input.tsx',
        group: 'form',
        summary: 'Separate date and time controls with a first-class null value and conversion through the supplied IANA zone.',
        states: [
            {
                id: 'empty',
                label: 'Empty',
                render: () => (_jsx(Field, { label: "Publicaci\u00F3n", children: (control) => _jsx(DateTimeInput, { ...control, value: null, zone: "America/Puerto_Rico" }) })),
            },
            {
                id: 'populated',
                label: 'Populated',
                render: () => (_jsx(Field, { label: "Publicaci\u00F3n", children: (control) => (_jsx(DateTimeInput, { ...control, value: "2026-06-15T13:30:00.000Z", zone: "America/Puerto_Rico" })) })),
            },
            {
                id: 'invalid',
                label: 'Invalid',
                render: () => (_jsx(Field, { label: "Publicaci\u00F3n", error: "Escribe fecha y hora.", children: (control) => _jsx(DateTimeInput, { ...control, value: null, zone: "America/Puerto_Rico" }) })),
            },
            {
                id: 'disabled',
                label: 'Disabled',
                render: () => (_jsx(Field, { label: "Publicaci\u00F3n", children: (control) => (_jsx(DateTimeInput, { ...control, value: "2026-06-15T13:30:00.000Z", zone: "America/Puerto_Rico", disabled: true })) })),
            },
        ],
    },
    {
        id: 'input',
        name: 'Input',
        module: 'input.tsx',
        group: 'form',
        summary: 'A text control reading its edge and ring from tokens. Invalid is a thicker high-contrast border, never a red one.',
        states: [
            {
                id: 'default',
                label: 'Default',
                render: () => _jsx(Input, { "aria-label": "Email", placeholder: "nombre@apuestapr.com" }),
            },
            {
                id: 'invalid',
                label: 'Invalid',
                render: () => _jsx(Input, { "aria-label": "Email", "aria-invalid": true, defaultValue: "no-es-un-email" }),
            },
            {
                id: 'disabled',
                label: 'Disabled',
                render: () => _jsx(Input, { "aria-label": "Email", disabled: true, defaultValue: "soporte@apuestapr.local" }),
            },
        ],
    },
    {
        id: 'label',
        name: 'Label',
        module: 'label.tsx',
        group: 'form',
        summary: 'A native label. The required mark is decoration; the control carries aria-required.',
        states: [
            {
                id: 'default',
                label: 'Default',
                render: () => (_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Label, { htmlFor: "registry-label-demo", requiredMark: true, children: "Contrase\u00F1a" }), _jsx(Input, { id: "registry-label-demo", type: "password" })] })),
            },
        ],
    },
    {
        id: 'field',
        name: 'Field',
        module: 'field.tsx',
        group: 'form',
        summary: 'Generates the ids so the label, the description, and the error are actually associated with the control.',
        states: [
            {
                id: 'default',
                label: 'With description',
                render: () => (_jsx(Field, { label: "Email", description: "El que usas para entrar al panel.", required: true, children: (control) => _jsx(Input, { type: "email", autoComplete: "username", ...control }) })),
            },
            {
                id: 'invalid',
                label: 'With error',
                render: () => (_jsx(Field, { label: "Email", error: "Escribe un email v\u00E1lido.", required: true, children: (control) => _jsx(Input, { type: "email", defaultValue: "no-es-un-email", ...control }) })),
            },
        ],
    },
    {
        id: 'dialog',
        name: 'Dialog',
        module: 'dialog.tsx',
        group: 'overlay',
        summary: 'Focus moves in, is trapped while open, and returns to the trigger on close — requirement 7.4.',
        states: [
            { id: 'closed', label: 'Trigger', render: () => _jsx(DialogExample, {}) },
            { id: 'open', label: 'Open', overlayOpen: true, render: () => _jsx(DialogExample, { defaultOpen: true }) },
        ],
    },
    {
        id: 'dropdown-menu',
        name: 'DropdownMenu',
        module: 'dropdown-menu.tsx',
        group: 'overlay',
        summary: 'The ARIA menu pattern: arrows move between items, Tab leaves, Escape closes and returns focus.',
        states: [
            { id: 'closed', label: 'Trigger', render: () => _jsx(MenuExample, {}) },
            { id: 'open', label: 'Open', overlayOpen: true, render: () => _jsx(MenuExample, { defaultOpen: true }) },
        ],
    },
    {
        id: 'skeleton',
        name: 'Skeleton',
        module: 'skeleton.tsx',
        group: 'feedback',
        summary: 'A loading placeholder shaped like the layout it stands in for, decorative and hidden from assistive technology.',
        states: [
            {
                id: 'line',
                label: 'A line of text',
                render: () => _jsx(Skeleton, { className: "h-4 w-40" }),
            },
            {
                id: 'row',
                label: 'A table row',
                render: () => (_jsxs("div", { className: "flex items-center gap-3", children: [_jsx(Skeleton, { className: "h-10 w-10 rounded-full" }), _jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(Skeleton, { className: "h-4 w-32" }), _jsx(Skeleton, { className: "h-3 w-20" })] })] })),
            },
        ],
    },
    {
        id: 'empty-state',
        name: 'EmptyState',
        module: 'empty-state.tsx',
        group: 'feedback',
        summary: 'A valid answer, not a fault: an icon, a message, and an optional caller-styled action.',
        states: [
            {
                id: 'default',
                label: 'No message, no action',
                render: () => _jsx(EmptyState, { icon: icons.inbox, title: "No hay solicitudes de soporte." }),
            },
            {
                id: 'with-action',
                label: 'With a description and an action',
                render: () => (_jsx(EmptyState, { icon: icons.layers, title: "No hay contenido.", description: "Todav\u00EDa no se ha creado ninguna entrada de este tipo.", action: _jsx(Button, { size: "sm", children: "Crear entrada" }) })),
            },
        ],
    },
    {
        id: 'stat-card',
        name: 'StatCard',
        module: 'stat-card.tsx',
        group: 'feedback',
        summary: 'One number, in context, for a dashboard landing page. Neutral or attention-toned.',
        states: [
            {
                id: 'default',
                label: 'Neutral',
                render: () => (_jsx(StatCard, { label: "Sin asignar", value: 7, icon: icons.inbox, description: "En la cola hoy" })),
            },
            {
                id: 'attention',
                label: 'Attention',
                render: () => (_jsx(StatCard, { label: "En espera de retenci\u00F3n", value: 2, icon: icons.clock, tone: "attention" })),
            },
            {
                id: 'loading',
                label: 'Loading',
                render: () => _jsx(StatCard, { label: "Sin asignar", value: 0, icon: icons.inbox, loading: true }),
            },
        ],
    },
    {
        id: 'toast',
        name: 'Toast',
        module: 'toast.tsx',
        group: 'feedback',
        summary: 'Mutation feedback with a pause-on-hover/focus auto-dismiss and an aria-live announcement, inherited from Radix.',
        states: [
            {
                id: 'default',
                label: 'Default',
                overlayOpen: true,
                render: () => (_jsxs(ToastProvider, { children: [_jsxs(Toast, { open: true, duration: Number.POSITIVE_INFINITY, children: [_jsx("div", { children: _jsx(ToastTitle, { children: "Solicitud guardada" }) }), _jsx(ToastClose, {})] }), _jsx(ToastViewport, {})] })),
            },
            {
                id: 'destructive',
                label: 'Destructive, with an action',
                overlayOpen: true,
                render: () => (_jsxs(ToastProvider, { children: [_jsxs(Toast, { open: true, variant: "destructive", duration: Number.POSITIVE_INFINITY, children: [_jsxs("div", { children: [_jsx(ToastTitle, { children: "Solicitud archivada" }), _jsx(ToastDescription, { children: "Puedes deshacerlo en los pr\u00F3ximos segundos." })] }), _jsx(ToastAction, { altText: "Deshacer el archivado", children: "Deshacer" }), _jsx(ToastClose, {})] }), _jsx(ToastViewport, {})] })),
            },
        ],
    },
];
/** Every module the registry claims. What a completeness test compares the directory against. */
export const registeredModules = primitiveRegistry.map((entry) => entry.module);
/** Every (primitive, state) pair, flattened. What the accessibility sweep iterates. */
export const registryStates = () => primitiveRegistry.flatMap((primitive) => primitive.states.map((state) => ({ primitive, state })));
//# sourceMappingURL=registry.js.map